import axios from 'axios';

import { api, camelizedAPIConfig } from '.';
import {
  MarketItemType,
  ReportObjectType,
  UniversalReason,
} from '../constants';
import {
  IBanner,
  ICategoryGroup,
  IChatCharacter,
  IKeyPagination,
  IMarketCuration,
  IMarketItem,
  INotification,
  IOtherUserMarketItemInventory,
  IPagination,
  IRecommentPaymentItem,
  IReportCategory,
  ISimpleUser,
  IStory,
  ITag,
  ITagDetail,
  IUser,
  VendorType,
} from '../model';
import { toQueryString } from '../utils/misc';
import { patchMyMarketItemInventory } from './user';

export type PostReasonsPayload = {
  reason: string | string[];
  rating?: number;
};

export const postReasons = (
  type: UniversalReason,
  { reason, rating }: PostReasonsPayload
) => {
  const reasons = typeof reason === 'string' ? [reason] : reason;
  return api.post('/universal-reasons/', { type, reasons, rating });
};

export const fetchCategories = async () => {
  const { data } = await api.get<ICategoryGroup[]>('/categories/');
  return data;
};

export const fetchMigrationData = async (key: string) => {
  const { data } = await api.get<{ data: unknown }>(
    `/supports/web-data-migrate/${key}/`
  );
  return data.data;
};

export const fetchBanners = async (key: string) => {
  const { data } = await api.get<IBanner[]>(
    `/banners/${toQueryString({ type: 'UV', key })}`
  );
  return data;
};

export const fetchCurrationBanners = async (type: string) => {
  const { data } = await api.get<IBanner[]>(`/curations/${type}/banners/`);
  return data;
};

export const fetchSearchTags = async (
  q: string,
  withProtected = true,
  lastEvaluatedKey = ''
) => {
  const { data } = await api.get<IKeyPagination<ITagDetail>>(
    `/es/tags/${toQueryString({ q, lastEvaluatedKey, p: withProtected })}`
  );
  return data;
};

export const fetchRecommendedTags = async () => {
  const { data } = await api.get<ITagDetail[]>('/recommended-tags/');
  return data;
};

export const postTag = async (name: string) => {
  const { data } = await api.post<ITag>('/tags/', { name });
  return data;
};

/**
 * Maintenance
 */

export const fetchMaintenance = async () => {
  const { data } = await axios.get<{ isMaintenance: boolean }>(
    process.env.NEXT_PUBLIC_MAINTENANCE_URL,
    camelizedAPIConfig
  );
  return data;
};

export const fetchNotifications = async (lastEvaluatedKey = '') => {
  const { data } = await api.get<IKeyPagination<INotification>>(
    `/v3/users/me/notifications/${toQueryString({ lastEvaluatedKey })}`
  );
  return data;
};

/**
 * Market
 */

export const fetchUserMarketItemInventories = async (ids: number[]) => {
  const { data } = await api.get<IOtherUserMarketItemInventory[]>(
    `/users/market-item-inventories/${toQueryString({ ids: ids.join(',') })}`
  );
  return data;
};

/**
 * Section
 */

export const fetchSectionStories = async (
  sectionId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IStory>>(
    `/sections/${sectionId}/stories/${toQueryString({ page, perPage })}`
  );
  return data;
};

export const fetchSectionUsers = async (
  sectionId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IUser>>(
    `/sections/${sectionId}/users/${toQueryString({ page, perPage })}`
  );
  return data;
};

export const fetchSectionBanners = async (sectionId: number) => {
  const { data } = await api.get<IBanner[]>(`/sections/${sectionId}/banners/`);
  return data;
};

/**
 * Market
 */

type IMarketItemPurchase = {
  itemId: number;
  optionId: number;
  isAutoApplyVoucher?: boolean;
};

export const postMarketItemPurchase = async ({
  itemId,
  optionId,
  isAutoApplyVoucher = false,
}: IMarketItemPurchase) => {
  const { data: inventory } = await api.post(
    `/store/market-items/${itemId}/options/${optionId}/purchase/`,
    { isAutoApplyVoucher }
  );
  return await patchMyMarketItemInventory({
    id: inventory.id,
    status: 'ACTIVE',
  });
};

export const fetchMarketCuration = async () => {
  const { data } = await api.get<IMarketCuration[]>('/store/market-curations/');
  return data;
};

export const fetchMarketItems = async (
  type: MarketItemType,
  page = 1,
  perPage = 18
) => {
  const { data } = await api.get<IPagination<IMarketItem>>(
    `/store/market-items/${toQueryString({ type, page, perPage })}`
  );
  return data;
};

export type UniversalBalloonUsagePayload = {
  balloon: number;
  transactionId: string;
  description: string;
};

export const postUniversalBalloonUsage = async (
  payload: UniversalBalloonUsagePayload
) => {
  const { data } = await api.post('/store/universal-use-balloon/', payload);
  return data;
};

/**
 * Honor Partnership
 */

type IHonorPartnership = {
  id: number;
  partner: ISimpleUser;
  startedAt: string;
  type: 'honor';
  representStory?: Pick<IStory, 'id' | 'title'>;
};

export const fetchHonorPartnershipPartners = async () => {
  const { data } = await api.get<IHonorPartnership[]>(
    '/partnerships/honor/partners/'
  );
  return data.sort(() => Math.random() - 0.5);
};

/**
 * Search
 */

type SearchAllResult = {
  stories?: IKeyPagination<IStory> & { count: number };
  users?: IKeyPagination<IUser> & { count: number };
  tags?: IKeyPagination<ITag> & { count: number };
  characters?: IKeyPagination<IChatCharacter> & { count: number };
};

export const fetchSearchAll = async (q: string, limit?: number) => {
  const { data } = await api.get<SearchAllResult>(
    `/es/all/${toQueryString({ q, ...(limit ? { limit } : {}) })}`
  );
  return data;
};

export const fetchSearchStories = async (q: string, lastEvaluatedKey = '') => {
  const { data } = await api.get<IKeyPagination<IStory>>(
    `/es/stories/${toQueryString({ q, lastEvaluatedKey })}`
  );
  return data;
};

export const fetchSearchCharacters = async (
  q: string,
  lastEvaluatedKey = ''
) => {
  const { data } = await api.get<
    IKeyPagination<
      Pick<
        IChatCharacter,
        | 'id'
        | 'name'
        | 'profile_image'
        | 'greeting'
        | 'tags'
        | 'stat'
        | 'information'
        | 'summary'
        | 'status'
      >
    >
  >(`/es/characters/${toQueryString({ q, lastEvaluatedKey })}`);
  return data;
};

export const fetchSearchUsers = async (q: string, lastEvaluatedKey = '') => {
  const { data } = await api.get<IKeyPagination<IUser>>(
    `/es/users/${toQueryString({ q, lastEvaluatedKey })}`
  );
  return data;
};

export const fetchPopularTags = async (withProtected = true) => {
  const { data } = await api.get<ITag[]>(
    `/popular-tags/${toQueryString({ withProtected })}`
  );
  return data;
};

// export const fetchRecommendation = async (
//   lastEvaluatedKey = '',
//   {
//     orderType,
//     simsType,
//     storyIds,
//   }: {
//     orderType?: string;
//     simsType?: string;
//     storyIds?: number;
//   }
// ) => {
//   const { data } = await api.get<IKeyPagination<IStory>>(
//     `/v4/recommendation-stories/${toQueryString({
//       lastEvaluatedKey,
//       orderType,
//       simsType,
//       storyIds,
//     })}`
//   );
//   return data;
// };

/**
 * Report
 */

export const fetchReportCategories = async () => {
  const { data } = await api.get<IReportCategory[]>('/report-categories/');
  return data;
};

export const postReport = async (
  objectType: ReportObjectType,
  objectId: number,
  categoryId: number,
  reason: string
) => {
  const { data } = await api.post('/reports/', {
    objectType,
    objectId,
    category: categoryId,
    reason,
  });
  return data;
};

/**
 * Donation
 */

export type IDonationCumulativeRanking = {
  rank: number;
  user: ISimpleUser;
  balloon: number;
};

export const fetchDonationCumulativeRankings = async (
  storyId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IDonationCumulativeRanking>>(
    `/my-stories/${storyId}/donation-cumulative-rankings/${toQueryString({
      page,
      perPage,
    })}`
  );
  return data;
};

export const fetchOthersDonationCumulativeRankings = async (
  storyId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IDonationCumulativeRanking>>(
    `/stories/${storyId}/donation-cumulative-rankings/${toQueryString({
      page,
      perPage,
    })}`
  );
  return data;
};

export const fetchRecommendPaymentItems = async (vendor: VendorType) => {
  const { data } = await api.get<IRecommentPaymentItem>(
    `/store/goods-items/recommend${toQueryString({ vendor })}`
  );
  return data;
};
